import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";

import TimeUtils from "utils/time";
import MonthlyActivityUtils from "utils/monthlyActivity";
import TugSession from "data/firestore/tugSession";
import { Typography, CircularProgress, Box } from "@mui/material";
import AnalyticsService from "data/firestore/analytics";
import { grey } from "@mui/material/colors";

// Per-cell label: "Xh Ym" / "Xm", blank for empty days, "<1m" for a nonzero
// sub-minute session so a colored square is never left unlabeled.
const formatCell = (ms) => {
  if (!ms) return "";
  const { hours, minutes } = TimeUtils.breakdown(ms, true);
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  return parts.length ? parts.join(" ") : "<1m";
};

const MonthlyActivityChart = ({ uid, month, dates = undefined }) => {
  const [data, setData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [loading, setLoading] = useState(true);

  const startOfMonth = moment(month).startOf("month").format("YYYY-MM-DD");
  const endOfMonth = moment(month).endOf("month").format("YYYY-MM-DD");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchSessionsForMonth, [uid, month, dates]);

  function fetchSessionsForMonth() {
    if (month === null) {
      return;
    }
    setLoading(true);

    // If dates are provided, use them directly
    if (dates) {
      setIsEmpty(!Object.keys(dates).length);
      setData(_.orderBy(dates, "date", "asc"));
      setLoading(false);
      return;
    }

    // Otherwise, fetch sessions traditionally
    TugSession.history(uid, month)
      .then((results) => {
        const sessions = _.orderBy(results, "date", "desc");
        setIsEmpty(!sessions.length);
        setData(
          _.orderBy(
            MonthlyActivityUtils.processData(
              startOfMonth,
              endOfMonth,
              sessions
            ),
            "date",
            "asc"
          )
        );
      })
      .finally(() => setLoading(false));
  }

  // Once loaded, a genuinely empty month shows the message.
  if (!loading && isEmpty) {
    return (
      <Typography variant="overline">No completed tug sessions yet</Typography>
    );
  }

  // Render the real grid once loaded; while loading, render the month's empty
  // grid (all cells 0) so the chart is already at its true height -- the number
  // of week-rows is the same whether or not there's data -- then overlay a
  // spinner. No skeleton height to guess and no layout jump when data arrives.
  const displayData = data.length
    ? data
    : _.orderBy(
        MonthlyActivityUtils.processData(startOfMonth, endOfMonth, []),
        "date",
        "asc"
      );

  const groupedData = MonthlyActivityUtils.groupByWeek(displayData);

  const sortedWeeks = _.sortBy(Object.keys(groupedData));
  const series = sortedWeeks.map((week) => ({
    name: MonthlyActivityUtils.formatWeekLabel(week),
    data: groupedData[week],
  }));

  const options = {
    chart: {
      type: "heatmap",
      height: 350,
      toolbar: {
        show: true, // Keep the toolbar visible
        tools: {
          download: false, // Hide the download option
          // other tools can be configured here as well
        },
      },
      // Additional options for tooltip
    },
    xaxis: {
      type: "category",
      position: "top",
      categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], // Days of the week
      tooltip: { enabled: false },
      lines: {
        show: true, // Show vertical grid lines
      },
    },
    yaxis: {
      show: false,
      reversed: true,
      tooltip: { enabled: false },
      lines: {
        show: true, // Show vertical grid lines
      },
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        let date = w.config.series[seriesIndex].data[dataPointIndex].date;
        let value = series[seriesIndex][dataPointIndex];

        if (
          !moment(date).isBetween(
            moment(startOfMonth),
            moment(endOfMonth),
            undefined,
            "[]"
          )
        ) {
          return "";
        }

        // Track hover event
        const formattedDate = moment(date).format("MMM D");
        const timeString = TimeUtils.elapsedToString(
          TimeUtils.breakdown(value)
        );

        AnalyticsService.throttleChartHover(
          "hover_month_heatmap",
          { date: date, value: value, formattedDate: formattedDate },
          `${formattedDate} ${timeString}`
        );

        return `<div class="apexcharts-tooltip-title" style="background: none; border-bottom: none;">${formattedDate}: ${timeString}</div>`;
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 0,
        useFillColorAsStroke: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatCell(val),
      style: {
        fontSize: "10px",
        fontWeight: 600,
      },
    },
    colors: ["#008FFB"],
  };

  return (
    <div
      id="chart"
      className="heatmap-with-labels"
      style={{
        position: "relative",
        backgroundColor: "white",
        borderRadius: 25,
      }}
    >
      {/* Black label with a white outline (paint-order draws the stroke behind
          the fill) so it stays readable on light or dark cells alike. */}
      <style>{`
        .heatmap-with-labels .apexcharts-datalabel {
          fill: ${grey[800]};
          stroke: #fff;
          stroke-width: 3px;
          stroke-linejoin: round;
          paint-order: stroke;
        }
      `}</style>
      <ReactApexChart options={options} series={series} type="heatmap" />
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.5)",
            borderRadius: "25px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </div>
  );
};

export default MonthlyActivityChart;
